/** Hierarchy Builder - Parses TOC HTML into structured data */
import type { HtmlParser } from '~/plugins/dom-parser'
import type { Page } from '~/utils/solr/literature'
import { getHierarchyLookupKeys, isExcludedHierarchyHref, resolvePageNumberFromHierarchyLookup } from './hierarchy-lookup'

/** TOC item representing a table of contents entry */
export interface TocItemData {
  id: string
  title: string
  pageNumberString?: string
  fragmentId?: string
  href?: string
  children?: TocItemData[]
}

export class HierarchyBuilder {
  private tocIdCounter = 0
  private readonly excludedHrefs = new Set<string>(['title.xhtml', 'colophon.xhtml', 'toc.xhtml'])
  constructor(private domParser: HtmlParser<Document>) { }

  /** Builds TOC hierarchy from Solr documents */
  buildEnhancedHierarchy(solrDocuments: Page[]): TocItemData[] {
    const bookNavDoc = solrDocuments.find(doc => doc.secondLevelNodeType === 'book_nav')
    if (!bookNavDoc) {
      console.warn('No document with TOC HTML found')
      return []
    }

    const bookTocLookupArray = solrDocuments.filter(doc => doc.secondLevelNodeType === 'book_toc' || doc.secondLevelNodeType === 'book_htmltoc').map(doc => doc.bodytext)
    const pageListLookup = this.buildPageListLookup(bookNavDoc.bodytext)
    if (bookTocLookupArray.length === 0) {
      console.error('No book_toc documents found, table of contents will not work as expected.')
    }
    const tocNodes = this.buildFromHtml(bookNavDoc.bodytext, bookTocLookupArray, pageListLookup)
    // Propagate page numbers from leaf nodes up to parents
    return this.propagatePageNumbers(tocNodes)
  }

  /** Parses TOC HTML into hierarchy structure */
  buildFromHtml(tocHtml: string, bookTocLookup?: string[], pageListLookup?: Map<string, string>): TocItemData[] {
    if (!tocHtml) {
      return []
    }

    const doc = this.domParser(`<div id="root">${tocHtml}</div>`)
    const root = doc.getElementById('root')
    if (!root) {
      return []
    }

    // Create lookup map for page numbers from bookTocLookup
    const pageNumberLookup = bookTocLookup ? this.buildPageNumberLookup(bookTocLookup) : new Map()
    if (pageListLookup) {
      for (const [key, pageNumber] of pageListLookup.entries()) {
        if (!pageNumberLookup.has(key)) {
          pageNumberLookup.set(key, pageNumber)
        }
      }
    }

    const topLevelOls = Array.from(root.querySelectorAll(':scope > ol')).filter(ol => !this.isPageListOl(ol))
    if (topLevelOls.length === 0) {
      const firstOl = root.querySelector('ol') || doc.querySelector('ol')
      return firstOl && !this.isPageListOl(firstOl) ? this.parseOl(firstOl, 0, pageNumberLookup) : []
    }

    const results: TocItemData[] = []
    topLevelOls.forEach((ol: Element) => results.push(...this.parseOl(ol, 0, pageNumberLookup)))
    return results
  }

  /**
   * Recursively propagates page numbers from leaf nodes up to parents using post-order traversal
   * This ensures that page numbers from the lowest level are copied to all parent nodes
   */
  private propagatePageNumbers(nodes: TocItemData[]): TocItemData[] {
    return nodes.map((node) => {
      // Process children first (post-order traversal)
      const processedChildren = node.children?.length
        ? this.propagatePageNumbers(node.children)
        : []

      // If this is a leaf node, use its own pageNumberString
      // If it has children, use the first child's pageNumberString that has one
      const propagatedPageNumber = processedChildren.length > 0
        ? processedChildren.find(child => child.pageNumberString)?.pageNumberString
        : node.pageNumberString

      return {
        ...node,
        pageNumberString: node.pageNumberString ?? propagatedPageNumber,
        children: processedChildren,
      }
    })
  }

  /**
   * Builds a lookup map from href to page number string from book_toc HTML snippets.
   *
   * This method creates a mapping that allows book_nav TOC items to lookup their page numbers
   * from book_toc or book_htmltoc documents. The book_nav contains the TOC hierarchy structure with href links,
   * while book_toc contains separate HTML snippets with the same href values but enriched with
   * page numbers in spans with class "lic".
   *
   * How the lookup works:
   * 1. Parses each HTML snippet from book_toc Solr documents
   * 2. Extracts href attributes from anchor tags and page numbers from child ".lic" spans
   * 3. Creates a Map<href, pageNumber> for O(1) lookups during TOC parsing
   * 4. During book_nav parsing, each TOC item's href is used as a key to retrieve its page number
   *
   * Example: If book_nav has `<a href="chapter1.xhtml">Chapter 1</a>` and book_toc has
   * `<a href="chapter1.xhtml"><span class="lic">42</span></a>`, the lookup maps "chapter1.xhtml" → "42"
   *
   * This enables the TOC hierarchy to display accurate page numbers from the book_toc indexing,
   * allowing users to see which physical page each TOC section corresponds to.
   *
   * @param bookTocLookup Array of HTML strings from book_toc Solr documents
   * @returns Map where keys are href strings and values are page number strings
   */
  private buildPageNumberLookup(bookTocLookup: string[]): Map<string, string> {
    const lookup = new Map<string, string>()

    if (!bookTocLookup || bookTocLookup.length === 0) {
      return lookup
    }

    // Parse each HTML snippet individually and combine results
    bookTocLookup.forEach((htmlSnippet) => {
      if (!htmlSnippet.trim()) {
        return // Skip empty snippets
      }

      const doc = this.domParser(htmlSnippet)

      // Find all anchor tags and extract href and page number
      const anchorTags = doc?.querySelectorAll('a[href]')
      anchorTags?.forEach((anchor: Element) => {
        const href = anchor.getAttribute('href')
        if (href) {
          // Find the span with class "lic" containing the page number
          const licSpan = anchor.querySelector('.lic')
          const pageNumber = licSpan?.textContent?.trim()

          if (pageNumber) {
            const hasFragment = href.includes('#')
            getHierarchyLookupKeys(href, { includeWithoutFragment: !hasFragment }).forEach(key => lookup.set(key, pageNumber))
          }
        }
      })
    })

    return lookup
  }

  private buildPageListLookup(navHtml: string): Map<string, string> {
    const lookup = new Map<string, string>()
    if (!navHtml) {
      return lookup
    }

    const doc = this.domParser(`<div id="root">${navHtml}</div>`)
    const root = doc.getElementById('root')
    if (!root) {
      return lookup
    }

    const navElements = Array.from(root.querySelectorAll('nav'))
      .filter(nav => (nav.getAttribute('epub:type') || '').toLowerCase() === 'page-list')

    // Indexing may strip the epub <nav> wrappers, leaving the page-list as a bare top-level <ol>
    const pageListContainers = navElements.length > 0
      ? navElements
      : Array.from(root.querySelectorAll(':scope > ol')).filter(ol => this.isPageListOl(ol))

    pageListContainers.forEach((container) => {
      const links = container.querySelectorAll('a[href]')
      links.forEach((link) => {
        const href = link.getAttribute('href')
        if (!href) {
          return
        }

        const pageNumber = link.textContent?.trim()
        if (!pageNumber || !this.isPageNumberLabel(pageNumber)) {
          return
        }

        const keys = getHierarchyLookupKeys(href)
        keys.forEach((key) => {
          if (!lookup.has(key)) {
            lookup.set(key, pageNumber)
          }
        })
      })
    })

    return lookup
  }

  /** Printed page label: arabic digits, or roman numerals used for front matter */
  private isPageNumberLabel(text: string): boolean {
    return /^\d+$/.test(text) || /^[ivxlcdm]+$/i.test(text)
  }

  /** An epub page-list rendered as a bare <ol>: every entry is just a page number */
  private isPageListOl(ol: Element): boolean {
    const links = Array.from(ol.querySelectorAll(':scope > li > a'))
    return links.length > 0 && links.every(link => this.isPageNumberLabel(link.textContent?.trim() ?? ''))
  }

  /** Parses ordered list into TOC items */
  private parseOl(ol: Element, level: number, pageNumberLookup: Map<string, string>): TocItemData[] {
    const items = Array.from(ol.children).filter(el => el.tagName.toLowerCase() === 'li') as HTMLElement[]
    return items.map((li) => {
      const link = li.querySelector(':scope > a')
      if (!link) {
        return null
      }

      const nestedOl = li.querySelector(':scope > ol')
      const href = link.getAttribute('href') || ''
      if (href && isExcludedHierarchyHref(href, this.excludedHrefs)) {
        return null
      }
      const fragmentId = href.includes('#') ? href.split('#')[1] : ''
      const titleSpan = link.querySelector('.toc')
      const title = titleSpan?.textContent?.trim() || link.textContent?.trim() || ''
      // Use the pageNumberLookup to get page number from bookTocLookup instead of TOC HTML
      const pageNumberString = href ? resolvePageNumberFromHierarchyLookup(href, pageNumberLookup) : undefined

      const node: TocItemData = {
        id: `toc-${this.tocIdCounter++}`,
        title,
        pageNumberString,
        href,
        fragmentId: fragmentId || undefined,
        children: [],
      }

      if (nestedOl) {
        node.children = this.parseOl(nestedOl, level + 1, pageNumberLookup)
      }
      return node
    }).filter(Boolean) as TocItemData[]
  }

  /** Validates TOC hierarchy structure */
  validateHierarchy(nodes: TocItemData[]): boolean {
    if (!Array.isArray(nodes)) {
      return false
    }
    return nodes.every(node => this.validateNode(node))
  }

  /** Validates individual TOC node */
  private validateNode(node: TocItemData): boolean {
    if (!node || typeof node !== 'object') {
      return false
    }
    if (!node.id || !node.title) {
      return false
    }

    if (node.pageNumberString !== undefined && typeof node.pageNumberString !== 'string') {
      return false
    }
    if (node.fragmentId !== undefined && typeof node.fragmentId !== 'string') {
      return false
    }

    if (node.children && node.children.length > 0) {
      return node.children.every((child: TocItemData) => this.validateNode(child))
    }

    return true
  }
}
